/**
 * Saved setup drafts: open one, duplicate it, delete it.
 *
 * There was no list because there was nothing to list — one draft, in one
 * browser, deleted on success. Now a draft outlives the campaign it made, which
 * is what makes duplicating useful: the next campaign is usually a variation of
 * the last one that worked, not a blank form.
 *
 * A draft that has already made a campaign is marked rather than hidden. It is
 * the most likely one to want a copy of.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  deleteCampaignDraft,
  duplicateCampaignDraft,
  listCampaignDrafts,
  type CampaignDraftRow,
  userFacingError,
} from '@/api/client';

export function DraftPicker({
  currentId,
  onOpen,
}: {
  /** The draft being edited, so the list can say which one that is. */
  currentId: string | null;
  onOpen: (row: CampaignDraftRow) => void;
}) {
  const [drafts, setDrafts] = useState<CampaignDraftRow[]>([]);
  // Distinct from `drafts.length === 0`: before the first fetch lands we do
  // not know the count, and a button that says "0" while still loading is
  // worse than one that says nothing yet.
  const [counted, setCounted] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { drafts: rows } = await listCampaignDrafts();
      setDrafts(rows);
      setCounted(true);
    } catch (e) {
      setError(
        userFacingError(e, 'Saved drafts couldn’t be loaded. Please try again.'),
      );
    }
  }, []);

  // On mount, not on open. The count is the whole answer to "is it worth
  // clicking this?", and fetching only on open meant the only way to learn
  // there was nothing saved was to open it and be told there was nothing
  // saved. Re-runs when the draft being edited changes id, which is what
  // happens the moment a new draft is first persisted.
  useEffect(() => {
    void refresh();
  }, [refresh, currentId]);

  const duplicate = async (id: string) => {
    setBusy(id);
    try {
      const row = await duplicateCampaignDraft(id);
      await refresh();
      // Open the copy: duplicating is something you do in order to edit it.
      onOpen(row);
      setOpen(false);
    } catch (e) {
      setError(
        userFacingError(e, 'That draft couldn’t be duplicated. Please try again.'),
      );
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    setBusy(id);
    try {
      await deleteCampaignDraft(id);
      await refresh();
    } catch (e) {
      setError(
        userFacingError(e, 'That draft couldn’t be deleted. Please try again.'),
      );
    } finally {
      setBusy(null);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="button drafts__trigger"
        onClick={() => setOpen(true)}
      >
        <DraftIcon />
        <span>Saved drafts</span>
        {counted ? (
          <span
            className="drafts__count"
            aria-label={`${drafts.length} saved ${drafts.length === 1 ? 'draft' : 'drafts'}`}
          >
            {drafts.length}
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <section className="drafts">
      <header className="drafts__head">
        <h3 className="history__heading" style={{ margin: 0, border: 0 }}>
          Saved drafts
        </h3>
        <span className="masthead__spacer" />
        <button type="button" className="button button--quiet" onClick={() => setOpen(false)}>
          Close
        </button>
      </header>

      {error ? <p className="history__error">{error}</p> : null}
      {drafts.length === 0 ? (
        <p className="history__empty">
          Nothing saved yet. Drafts save themselves as you type, and are kept
          after you create the campaign.
        </p>
      ) : null}

      <ul className="drafts__list">
        {drafts.map((d) => (
          <li key={d.id} className="drafts__row">
            <div className="drafts__main">
              <strong>{d.name || 'Untitled draft'}</strong>
              {d.id === currentId ? (
                <span className="init__tag">editing</span>
              ) : null}
              <span className="ref__dim">
                {' '}
                {when(d.updated_at)}
                {d.created_campaign_id
                  ? ` · made ${d.created_campaign_name || 'a campaign'}`
                  : ''}
                {d.source_draft_id ? ' · copy' : ''}
              </span>
            </div>
            <div className="drafts__actions">
              <button
                type="button"
                className="button button--quiet"
                disabled={busy === d.id || d.id === currentId}
                onClick={() => {
                  onOpen(d);
                  setOpen(false);
                }}
              >
                Open
              </button>
              <button
                type="button"
                className="button button--quiet"
                disabled={busy === d.id}
                onClick={() => void duplicate(d.id)}
              >
                Duplicate
              </button>
              <button
                type="button"
                className="button button--ghost"
                disabled={busy === d.id}
                onClick={() => void remove(d.id)}
                title="Delete this draft"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DraftIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3h8l3 3v10.5A1.5 1.5 0 0 1 15.5 18h-9A1.5 1.5 0 0 1 5 16.5v-12A1.5 1.5 0 0 1 6.5 3Z" />
      <path d="M13 3v4h4M8 11h6M8 14h4" />
      <path d="M3 6v10" />
    </svg>
  );
}

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
